import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { ArrowLeft, ChevronLeft, Plus } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  useCreateHallway,
  useCreatePosition,
  useCreatePositionsForRack,
  useCreateRack,
  useCreateRacksForHallway,
  useDeleteHallway,
  useDeletePosition,
  useDeleteRack,
  useUpdateHallway,
  useUpdatePosition,
  useUpdateRack,
  useWarehouses,
} from "~/hooks/wms";
import { useWarehouseInventory } from "~/hooks/wms/useInventory";
import type {
  Hallway,
  HallwayFormData,
  Position,
  PositionFormData,
  Rack,
  RackFormData,
} from "~/types/wms";

import { BulkPositionsDialog } from "./components/BulkPositionsDialog";
import { BulkRacksDialog } from "./components/BulkRacksDialog";
import { HallwayDialog } from "./components/HallwayDialog";
import { HallwayListPanel } from "./components/HallwayListPanel";
import { PositionDialog } from "./components/PositionDialog";
import { RackDetailPanel } from "./components/RackDetailPanel";
import { RackDialog } from "./components/RackDialog";
import { RackListPanel } from "./components/RackListPanel";

type MobileViewLevel = "hallways" | "racks" | "detail";

export function AllLocationsPage() {
  const navigate = useNavigate();

  // Selection state for 3-panel navigation
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>("");
  const [selectedHallwayId, setSelectedHallwayId] = useState<string | null>(
    null
  );
  const [selectedRackId, setSelectedRackId] = useState<string | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(
    null
  );

  // Mobile view level state
  const [mobileViewLevel, setMobileViewLevel] =
    useState<MobileViewLevel>("hallways");

  // Dialog state
  const [hallwayDialogOpen, setHallwayDialogOpen] = useState(false);
  const [rackDialogOpen, setRackDialogOpen] = useState(false);
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);
  const [bulkPositionsDialogOpen, setBulkPositionsDialogOpen] = useState(false);
  const [bulkRacksDialogOpen, setBulkRacksDialogOpen] = useState(false);

  // Edit state
  const [editingHallway, setEditingHallway] = useState<Hallway | undefined>();
  const [editingRack, setEditingRack] = useState<Rack | undefined>();
  const [editingPosition, setEditingPosition] = useState<
    Position | undefined
  >();

  // Delete confirmation state
  const [hallwayToDelete, setHallwayToDelete] = useState<Hallway | null>(null);
  const [rackToDelete, setRackToDelete] = useState<Rack | null>(null);
  const [positionToDelete, setPositionToDelete] = useState<Position | null>(
    null
  );

  // Data queries
  const { data: warehouses = [] } = useWarehouses();
  const { data: warehouseInventory, isLoading: isLoadingInventory } =
    useWarehouseInventory(selectedWarehouseId || undefined);

  // Set default warehouse when warehouses load
  useEffect(() => {
    if (warehouses.length > 0 && !selectedWarehouseId) {
      setSelectedWarehouseId(warehouses[0].id);
    }
  }, [warehouses, selectedWarehouseId]);

  // Mutations
  const createHallway = useCreateHallway();
  const updateHallway = useUpdateHallway();
  const deleteHallway = useDeleteHallway();
  const createRack = useCreateRack();
  const updateRack = useUpdateRack();
  const deleteRack = useDeleteRack();
  const createPosition = useCreatePosition();
  const updatePosition = useUpdatePosition();
  const deletePosition = useDeletePosition();
  const createPositionsForRack = useCreatePositionsForRack();
  const createRacksForHallway = useCreateRacksForHallway();

  // Selection handlers with cascade reset
  const handleWarehouseChange = (value: string) => {
    setSelectedWarehouseId(value);
    setSelectedHallwayId(null);
    setSelectedRackId(null);
    setSelectedPositionId(null);
    setMobileViewLevel("hallways");
  };

  const handleHallwaySelect = (hallwayId: string) => {
    setSelectedHallwayId(hallwayId);
    setSelectedRackId(null);
    setSelectedPositionId(null);
    setMobileViewLevel("racks");
  };

  const handleRackSelect = (rackId: string) => {
    setSelectedRackId(rackId);
    setSelectedPositionId(null);
    setMobileViewLevel("detail");
  };

  const handlePositionClick = (positionId: string) => {
    setSelectedPositionId((prev) => (prev === positionId ? null : positionId));
  };

  // Mobile back navigation handlers
  const handleBackToHallways = () => {
    setMobileViewLevel("hallways");
  };

  const handleBackToRacks = () => {
    setMobileViewLevel("racks");
  };

  // Get current hallways and racks from warehouse inventory
  const hallways = warehouseInventory?.hallways || [];
  const selectedHallwayData = hallways.find(
    (h) => h.hallway.id === selectedHallwayId
  );
  const racksInHallway = selectedHallwayData?.racks || [];
  const selectedRackData =
    racksInHallway.find((r) => r.rack.id === selectedRackId) || null;

  // Handlers for creating
  const handleCreateHallway = () => {
    setEditingHallway(undefined);
    setHallwayDialogOpen(true);
  };

  const handleCreateRack = () => {
    setEditingRack(undefined);
    setRackDialogOpen(true);
  };

  const handleCreatePosition = () => {
    setEditingPosition(undefined);
    setPositionDialogOpen(true);
  };

  // Handlers for editing
  const handleEditHallway = (hallway: Hallway) => {
    setEditingHallway(hallway);
    setHallwayDialogOpen(true);
  };

  const handleEditRack = (rack: Rack) => {
    setEditingRack(rack);
    setRackDialogOpen(true);
  };

  const handleEditPosition = (position: Position) => {
    setEditingPosition(position);
    setPositionDialogOpen(true);
  };

  // Delete confirmation handlers
  const confirmDeleteHallway = () => {
    if (hallwayToDelete) {
      deleteHallway.mutate(hallwayToDelete.id, {
        onSuccess: () => {
          setHallwayToDelete(null);
          if (selectedHallwayId === hallwayToDelete.id) {
            setSelectedHallwayId(null);
            setSelectedRackId(null);
            setSelectedPositionId(null);
          }
        },
      });
    }
  };

  const confirmDeleteRack = () => {
    if (rackToDelete) {
      deleteRack.mutate(rackToDelete.id, {
        onSuccess: () => {
          setRackToDelete(null);
          if (selectedRackId === rackToDelete.id) {
            setSelectedRackId(null);
            setSelectedPositionId(null);
          }
        },
      });
    }
  };

  const confirmDeletePosition = () => {
    if (positionToDelete) {
      deletePosition.mutate(positionToDelete.id, {
        onSuccess: () => {
          setPositionToDelete(null);
          if (selectedPositionId === positionToDelete.id) {
            setSelectedPositionId(null);
          }
        },
      });
    }
  };

  // Submit handlers
  const handleSubmitHallway = (data: HallwayFormData) => {
    if (editingHallway) {
      updateHallway.mutate(
        { id: editingHallway.id, data },
        { onSuccess: () => setHallwayDialogOpen(false) }
      );
    } else {
      createHallway.mutate(data, {
        onSuccess: () => setHallwayDialogOpen(false),
      });
    }
  };

  const handleSubmitRack = (data: RackFormData) => {
    if (editingRack) {
      updateRack.mutate(
        { id: editingRack.id, data },
        { onSuccess: () => setRackDialogOpen(false) }
      );
    } else {
      createRack.mutate(data, {
        onSuccess: () => setRackDialogOpen(false),
      });
    }
  };

  const handleSubmitPosition = (data: PositionFormData) => {
    if (editingPosition) {
      updatePosition.mutate(
        { id: editingPosition.id, data },
        { onSuccess: () => setPositionDialogOpen(false) }
      );
    } else {
      createPosition.mutate(data, {
        onSuccess: () => setPositionDialogOpen(false),
      });
    }
  };

  const handleBulkCreatePositions = (levels: number) => {
    if (selectedRackId) {
      createPositionsForRack.mutate(
        { rackId: selectedRackId, levels },
        { onSuccess: () => setBulkPositionsDialogOpen(false) }
      );
    }
  };

  const handleBulkCreateRacks = (count: number) => {
    if (selectedHallwayId) {
      createRacksForHallway.mutate(
        { hallwayId: selectedHallwayId, count },
        { onSuccess: () => setBulkRacksDialogOpen(false) }
      );
    }
  };

  // Get mobile view title and back handler
  const getMobileViewTitle = () => {
    switch (mobileViewLevel) {
      case "hallways":
        return "Pasillos";
      case "racks":
        return `Racks - ${selectedHallwayData?.hallway.code || ""}`;
      case "detail":
        return `Detalle - ${selectedRackData?.rack.full_code || ""}`;
    }
  };

  const handleMobileBack = () => {
    switch (mobileViewLevel) {
      case "racks":
        handleBackToHallways();
        break;
      case "detail":
        handleBackToRacks();
        break;
    }
  };

  // Mobile action buttons based on current view level
  const renderMobileActions = () => {
    if (mobileViewLevel === "hallways" && selectedWarehouseId) {
      return (
        <Button size="sm" variant="outline" onClick={handleCreateHallway}>
          <Plus className="h-4 w-4" />
        </Button>
      );
    }
    if (mobileViewLevel === "racks" && selectedHallwayId) {
      return (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleCreateRack}>
            <Plus className="mr-1 h-4 w-4" />
            Rack
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBulkRacksDialogOpen(true)}
          >
            <Plus className="mr-1 h-4 w-4" />
            Lote
          </Button>
        </div>
      );
    }
    if (mobileViewLevel === "detail" && selectedRackId) {
      return (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBulkPositionsDialogOpen(true)}
          >
            <Plus className="mr-1 h-4 w-4" />
            Lote
          </Button>
          <Button size="sm" onClick={handleCreatePosition}>
            <Plus className="mr-1 h-4 w-4" />
            Pos
          </Button>
        </div>
      );
    }
    return null;
  };

  return (
    <PageContainer className="space-y-4 lg:space-y-6">
      {/* Compact Header (< 1024px) */}
      <div className="space-y-3 lg:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={
                mobileViewLevel !== "hallways"
                  ? handleMobileBack
                  : () => navigate("/wms")
              }
              className="h-8 px-2"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">{getMobileViewTitle()}</h1>
            </div>
          </div>
          {renderMobileActions()}
        </div>

        <Select
          value={selectedWarehouseId}
          onValueChange={handleWarehouseChange}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Seleccionar bodega" />
          </SelectTrigger>
          <SelectContent>
            {warehouses.map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {w.code} - {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop Header (>= 1024px) */}
      <PageHeader
        className="hidden lg:block"
        title="Ubicaciones"
        description="Explorador de pasillos, racks y posiciones"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <>
            <Select
              value={selectedWarehouseId}
              onValueChange={handleWarehouseChange}
            >
              <SelectTrigger className="h-9 w-[200px]">
                <SelectValue placeholder="Seleccionar bodega" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.code} - {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedWarehouseId && (
              <Button size="sm" variant="outline" onClick={handleCreateHallway}>
                <Plus className="mr-1 h-4 w-4" />
                Pasillo
              </Button>
            )}
            {selectedHallwayId && (
              <>
                <Button size="sm" variant="outline" onClick={handleCreateRack}>
                  <Plus className="mr-1 h-4 w-4" />
                  Rack
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkRacksDialogOpen(true)}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Racks en Lote
                </Button>
              </>
            )}
            {selectedRackId && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkPositionsDialogOpen(true)}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  En Lote
                </Button>
                <Button size="sm" onClick={handleCreatePosition}>
                  <Plus className="mr-1 h-4 w-4" />
                  Posición
                </Button>
              </>
            )}
          </>
        }
      />

      {/* Compact Layout - Single Panel (< 1024px) */}
      <div className="h-[calc(100vh-200px)] lg:hidden">
        {mobileViewLevel === "hallways" && (
          <HallwayListPanel
            hallways={hallways}
            selectedHallwayId={selectedHallwayId}
            onSelectHallway={handleHallwaySelect}
            onEditHallway={handleEditHallway}
            onDeleteHallway={setHallwayToDelete}
            isLoading={isLoadingInventory}
          />
        )}
        {mobileViewLevel === "racks" && (
          <RackListPanel
            racks={racksInHallway}
            selectedRackId={selectedRackId}
            selectedPositionId={selectedPositionId}
            onSelectRack={handleRackSelect}
            onPositionClick={handlePositionClick}
            onEditRack={handleEditRack}
            onDeleteRack={setRackToDelete}
            hallwayCode={selectedHallwayData?.hallway.code}
            isLoading={isLoadingInventory}
          />
        )}
        {mobileViewLevel === "detail" && (
          <RackDetailPanel
            rackData={selectedRackData}
            selectedPositionId={selectedPositionId}
            onPositionClick={handlePositionClick}
            onClosePositionDetail={() => setSelectedPositionId(null)}
            onEditRack={
              selectedRackData
                ? () => handleEditRack(selectedRackData.rack)
                : undefined
            }
            onDeleteRack={
              selectedRackData
                ? () => setRackToDelete(selectedRackData.rack)
                : undefined
            }
            onEditPosition={handleEditPosition}
            onDeletePosition={setPositionToDelete}
            isLoading={isLoadingInventory}
          />
        )}
      </div>

      {/* Desktop Layout - 3-Panel Grid (>= 1024px) */}
      <div className="hidden h-[calc(100vh-180px)] grid-cols-12 gap-4 lg:grid">
        <div className="col-span-3">
          <HallwayListPanel
            hallways={hallways}
            selectedHallwayId={selectedHallwayId}
            onSelectHallway={handleHallwaySelect}
            onEditHallway={handleEditHallway}
            onDeleteHallway={setHallwayToDelete}
            isLoading={isLoadingInventory}
          />
        </div>

        <div className="col-span-4">
          <RackListPanel
            racks={racksInHallway}
            selectedRackId={selectedRackId}
            selectedPositionId={selectedPositionId}
            onSelectRack={handleRackSelect}
            onPositionClick={handlePositionClick}
            onEditRack={handleEditRack}
            onDeleteRack={setRackToDelete}
            hallwayCode={selectedHallwayData?.hallway.code}
            isLoading={isLoadingInventory}
          />
        </div>

        <div className="col-span-5">
          <RackDetailPanel
            rackData={selectedRackData}
            selectedPositionId={selectedPositionId}
            onPositionClick={handlePositionClick}
            onClosePositionDetail={() => setSelectedPositionId(null)}
            onEditRack={
              selectedRackData
                ? () => handleEditRack(selectedRackData.rack)
                : undefined
            }
            onDeleteRack={
              selectedRackData
                ? () => setRackToDelete(selectedRackData.rack)
                : undefined
            }
            onEditPosition={handleEditPosition}
            onDeletePosition={setPositionToDelete}
            isLoading={isLoadingInventory}
          />
        </div>
      </div>

      {/* Dialogs */}
      <HallwayDialog
        open={hallwayDialogOpen}
        onOpenChange={setHallwayDialogOpen}
        hallway={editingHallway}
        warehouseId={selectedWarehouseId || undefined}
        onSubmit={handleSubmitHallway}
        isLoading={createHallway.isPending || updateHallway.isPending}
      />

      <RackDialog
        open={rackDialogOpen}
        onOpenChange={setRackDialogOpen}
        rack={editingRack}
        hallwayId={selectedHallwayId || undefined}
        onSubmit={handleSubmitRack}
        isLoading={createRack.isPending || updateRack.isPending}
      />

      <PositionDialog
        open={positionDialogOpen}
        onOpenChange={setPositionDialogOpen}
        position={editingPosition}
        rackId={selectedRackId || undefined}
        onSubmit={handleSubmitPosition}
        isLoading={createPosition.isPending || updatePosition.isPending}
      />

      <BulkPositionsDialog
        open={bulkPositionsDialogOpen}
        onOpenChange={setBulkPositionsDialogOpen}
        rackCode={selectedRackData?.rack.full_code}
        onSubmit={handleBulkCreatePositions}
        isLoading={createPositionsForRack.isPending}
      />

      <BulkRacksDialog
        open={bulkRacksDialogOpen}
        onOpenChange={setBulkRacksDialogOpen}
        hallwayCode={selectedHallwayData?.hallway.full_code}
        onSubmit={handleBulkCreateRacks}
        isLoading={createRacksForHallway.isPending}
      />

      {/* Delete confirmation dialogs */}
      <AlertDialog
        open={!!hallwayToDelete}
        onOpenChange={(open) => !open && setHallwayToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Pasillo</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea eliminar el pasillo "
              {hallwayToDelete?.full_code}"? Esta acción eliminará también todos
              los racks y posiciones contenidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteHallway}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!rackToDelete}
        onOpenChange={(open) => !open && setRackToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Rack</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea eliminar el rack "
              {rackToDelete?.full_code}"? Esta acción eliminará también todas
              las posiciones contenidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteRack}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!positionToDelete}
        onOpenChange={(open) => !open && setPositionToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Posición</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea eliminar la posición "
              {positionToDelete?.full_code}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeletePosition}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
